import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import InspectionPhoto from 'models/InspectionPhoto'
import InputStore from 'stores/InputStore'
import { EMAIL } from 'util/validationMapping'

export const INSPECTION_TAG = {
    FRONT: 'front',
    REAR: 'rear',
    LEFT_SIDE: 'leftSide',
    RIGHT_SIDE: 'rightSide',
    ROOF: 'roof',
    VTV: 'vehicleTechnicalVerification',
    SPARE_WHEEL: 'spareTire',
    VEHICLE_REGISTRATION: 'vehicleTitle',
    WINDSHIELD_INTERIOR: 'insideFrontWindshield',
    NON_ROLLING_CERTIFICATE: 'nonRollingCertificate',
    ODOMETER: 'odometer',
    OBLEA_GNC: 'gncSticker',
    TUBO_GNC: 'gncCylinder',
    MOTORCYCLE_FRONT: 'motorcycleFront',
    MOTORCYCLE_REAR: 'motorcycleRear',
    MOTORCYCLE_LEFT_SIDE: 'motorcycleLeftSide',
    MOTORCYCLE_RIGHT_SIDE: 'motorcycleRightSide',
}

export const INSPECTION_STATUS = {
    PENDING: 'pending',
    IN_REVIEW: 'inReview',
    FINISHED: 'finished',
    REJECTED: 'rejected',
    CANCELED: 'canceled',
}

class InspectionStore {
    constructor(parentStore) {
        this.parentStore = parentStore

        this.inspection = {
            id: null,
            status: { id: null, value: '' },
            comment: '',
            files: [],
            history: [],
        }
        this.inspectionFiles = {}
        this.email = new InputStore(EMAIL)
        this.missingPhotoTagIds = {}

        makeObservable(this, {
            inspection: observable,
            inspectionFiles: observable,
            email: observable,
            missingPhotoTagIds: observable,
            setInspection: action,
            setInspectionFiles: action,
            setEmail: action,
            resetInspection: action,
            markMissingPhotos: action,
            clearMissingPhotoTag: action,
            shouldSendInspection: computed,
        })
    }

    setInspection(value) {
        this.inspection = {
            id: value?.id,
            status: value?.status,
            comment: value?.comment,
            files: value?.files,
            history: value?.history,
        }

        this.parentStore.policyStore.applyFromInspectionPolicy(value?.policy)
        this.email.setValue(value?.email)
    }

    setInspectionFiles(value) {
        this.inspectionFiles = value
    }

    setEmail(value) {
        this.email.setValue(value)
    }

    async getInspectionFromCustomer() {
        const store = this.parentStore

        store.preRequest(store.getInspectionFromCustomer)
        await store.plate?.validate()

        if (store.plate.error) {
            store.finishRequest()
            return false
        }

        try {
            const customer = { identityNumber: store.identityNumber?.value, plate: store.plate?.value }
            const { data: inspectionData } = await store.customerSelfManagementService.getInspectionId(customer)

            runInAction(() => {
                store.onSuccessRequest()
            })

            return inspectionData?.id
        } catch (error) {
            store.onErrorRequest(error?.message ? error?.message : 'Error inesperado, intente nuevamente')
            return false
        }
    }

    async getInspectionFromPolicy(policyId) {
        const store = this.parentStore

        store.preRequest(store.getInspectionFromPolicy)

        try {
            const { data: inspectionData } = await store.customerSelfManagementService.getInspectionId({ policyId })

            runInAction(() => {
                store.onSuccessRequest()
            })

            return inspectionData?.id
        } catch (error) {
            store.onErrorRequest(error?.message ? error?.message : 'Error inesperado, intente nuevamente')
            return false
        }
    }

    async getInspection(id) {
        const store = this.parentStore

        store.preRequest(store.getInspection)

        try {
            const { data: inspectionData } = await store.customerSelfManagementService.getInspection(id)

            runInAction(() => {
                this.setInspection(inspectionData)
                this.getInspectionFiles()
                store.onSuccessRequest()
            })

            return true
        } catch (error) {
            store.onErrorRequest(error?.message ? error?.message : 'Error inesperado, intente nuevamente')
            this.resetInspection()
            return false
        }
    }

    markMissingPhotos() {
        const missing = {}

        Object.keys(this.inspectionFiles).forEach((tagId) => {
            const fileStore = this.inspectionFiles[tagId]?.file

            if (!fileStore?.fileSelected || fileStore?.error) {
                missing[tagId] = true
            }
        })

        this.missingPhotoTagIds = missing

        return Object.keys(missing).length === 0
    }

    clearMissingPhotoTag(tagId) {
        if (!this.missingPhotoTagIds[tagId]) {
            return
        }

        const next = { ...this.missingPhotoTagIds }
        delete next[tagId]
        this.missingPhotoTagIds = next
    }

    async sendInspection() {
        const store = this.parentStore

        store.preRequest(store.sendInspection)
        store.clearErrors()

        const photosComplete = this.markMissingPhotos()
        await this.email?.validate()

        if (!photosComplete || this.email.error) {
            store.finishRequest()
            return false
        }

        try {
            const uploadInspection = this.json()
            await store.customerSelfManagementService.uploadInspection(uploadInspection)

            runInAction(() => {
                store.onSuccessRequest()
            })

            return true
        } catch (err) {
            store.onErrorRequest(err?.message ? err?.message : 'Error inesperado, intente nuevamente')
            return false
        }
    }

    getInspectionFiles() {
        const inspectionFilesObject = {}

        if (this.inspection?.status?.id === INSPECTION_STATUS.PENDING) {
            const inspectionFiles = this.inspection?.files?.map(file => file.tag)
            inspectionFiles?.forEach(tag => {
                inspectionFilesObject[tag.id] = new InspectionPhoto()
                inspectionFilesObject[tag.id].tag = { id: tag.id, value: tag.value }
            })
        }

        if (this.inspection?.status?.id === INSPECTION_STATUS.REJECTED) {
            const rejectedFiles = this.inspection?.files?.filter(
                file => file?.fileStatus?.id === INSPECTION_STATUS.REJECTED
            )
            rejectedFiles?.forEach(file => {
                inspectionFilesObject[file.tag.id] = new InspectionPhoto()
                inspectionFilesObject[file.tag.id].tag = { id: file.tag.id, value: file.tag.value }
            })
        }

        if (this.inspection?.status?.id === INSPECTION_STATUS.FINISHED) {
            this.inspection?.files?.forEach(fileObj => {
                inspectionFilesObject[fileObj?.tag?.id] = {
                    file: fileObj?.file,
                    fileStatus: fileObj?.fileStatus,
                    tag: fileObj?.tag,
                }
            })
        }

        this.setInspectionFiles(inspectionFilesObject)
        return inspectionFilesObject
    }

    get shouldSendInspection() {
        return Object.keys(this.inspectionFiles).every(tag => {
            const file = this.inspectionFiles[tag]
            return file.file?.error !== true && file.file?.file !== null
        })
    }

    resetInspection() {
        this.setInspection({
            id: null,
            status: null,
            comment: '',
            files: [],
            history: [],
        })
        this.setInspectionFiles({})
        this.missingPhotoTagIds = {}
    }

    json() {
        const files = Object.keys(this.inspectionFiles).map(key => ({
            tag: key,
            file: this.inspectionFiles[key].file.fileBase64,
        }))

        return {
            inspectionId: this.inspection?.id,
            email: this.email?.value,
            files,
        }
    }
}

export default InspectionStore
